/**
 * ChatBubble.tsx - Chat Message Display Component
 * Refactored: Extracted streaming logic to usePostStreamingState hook
 */

import { SmartProse } from "@/ui/components/ui/SmartProse";
import { formatDate } from '@/utilities/formatDate';
import { Accordion, Box, Card, For, HStack, Text, VStack } from "@chakra-ui/react";
import { t } from "i18next";
import { VectorSearchResult } from "./VectorSearchResult";
import { Post } from "@/types/props";
import { VectorSearchChunk } from "@/types/post.types";
import { useAutoPlayAudio, useChatContext } from '@/contexts/ChatContext';
import { useFilterContext } from '@/contexts/FilterContext';
import React from 'react';
import iqraSVG from '/iqra.svg';
import { MdError } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { FilterChips } from './filters/FilterChips';
import { sanitizeMalformedLinks } from '@/utilities/senitizeMalformedLinks';
import { useMarkdown } from "@/hooks/useMarkdown";
import { TTSAudioPlayer } from './TTSAudioPlayer';
import { usePostStreamingState } from '@/hooks/usePostStreamingState';

interface ChatBubbleProps {
    post: Post;
    boxOrientation: "left" | "right";
    shouldFade?: boolean;
    isNewMessage?: boolean;
}

// Component for rendering AI message content with markdown support
const AIMessageContent: React.FC<{ content: string; isStreaming: boolean }> = React.memo(
    ({ content, isStreaming }) => {
        const { setPendingQuestion, loading } = useChatContext();
        const { html: parsedHtml } = useMarkdown(content, {
            isStreaming,
            debounceMs: isStreaming ? 30 : 0,
        });

        const displayHtml = sanitizeMalformedLinks(parsedHtml || content);

        const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
            const span = (e.target as HTMLElement).closest('[data-question="true"]');
            if (!span || loading || isStreaming) return;
            const question = span.textContent?.trim();
            if (question) setPendingQuestion(question);
        };

        return (
            <Box
                className="chakra-card__description"
                css={{
                    color: "fg.muted",
                    "& [data-question='true']": {
                        cursor: "pointer",
                        color: "var(--cair-accent)",
                        textDecoration: "underline",
                        textUnderlineOffset: "2px",
                    },
                    "& [data-question='true']:hover": { opacity: 0.8 },
                }}
                onClick={handleClick}
            >
                <SmartProse htmlContent={displayHtml} dir="auto" maxWidth="unset">
                    <div dangerouslySetInnerHTML={{ __html: displayHtml }} />
                </SmartProse>
            </Box>
        );
    }
);

// Sources accordion component
const SourcesAccordion: React.FC<{ sources: VectorSearchChunk[]; postId: string; isRTL: boolean }> = React.memo(
    ({ sources, postId, isRTL }) => (
        <Box
            width="100%"
            marginBottom="3"
            borderRadius="var(--cair-radius-md)"
            background="var(--cair-surface)"
            overflow="hidden"
            transition="background-color 0.2s, box-shadow 0.2s"
            _hover={{ backgroundColor: "var(--cair-accent-subtle)", boxShadow: "var(--cair-shadow-sm)" }}
        >
            <Accordion.Root width="100%" collapsible>
                <Accordion.Item value="" border="0">
                    <Accordion.ItemTrigger
                        width="100%"
                        justifyContent="space-between"
                        cursor="pointer"
                        paddingX="4"
                        paddingY="3"
                        _hover={{ backgroundColor: "var(--cair-accent-subtle)" }}
                        transition="background-color 0.2s"
                        flexDirection={isRTL ? "row-reverse" : "row"}
                    >
                        <HStack gap="2" flexDirection={isRTL ? "row-reverse" : "row"}>
                            <img src={iqraSVG} />
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                {t("sources")} ({sources.length})
                            </Text>
                        </HStack>
                        <Accordion.ItemIndicator />
                    </Accordion.ItemTrigger>
                    <Accordion.ItemContent paddingX="4" paddingBottom="3" maxHeight="300px" overflowY="auto">
                        <VStack gap={2} marginTop="2">
                            <For each={sources}>
                                {(item, index) => (
                                    <VectorSearchResult key={`${postId}-chunk-${index}`} chunk={item} />
                                )}
                            </For>
                        </VStack>
                    </Accordion.ItemContent>
                </Accordion.Item>
            </Accordion.Root>
        </Box>
    )
);

// Error message component
const ErrorMessage: React.FC<{ message?: string; isRTL: boolean }> = React.memo(({ message, isRTL }) => (
    <Box width="100%" marginBottom="3" padding="4" borderRadius="lg" backgroundColor="red.50">
        <HStack gap="3" alignItems="flex-start" flexDirection={isRTL ? "row-reverse" : "row"}>
            <Box color="red.600" fontSize="lg" marginTop="1"><MdError /></Box>
            <VStack gap="1" alignItems={isRTL ? "flex-end" : "flex-start"} flex="1">
                <Text fontSize="sm" fontWeight="semibold" color="red.800">{t("AI Processing Error")}</Text>
                <Text fontSize="sm" color="red.700">{message || t("An error occurred while processing your request")}</Text>
            </VStack>
        </HStack>
    </Box>
));

export const ChatBubble: React.FC<ChatBubbleProps> = React.memo(
    ({ post, boxOrientation, shouldFade: _shouldFade = false, isNewMessage = false }) => {
        const { filterSuggestions } = useFilterContext();
        const { i18n } = useTranslation();
        const isRTL = i18n.dir() === 'rtl';
        const { autoPlayAudio } = useAutoPlayAudio();

        // Derive content states
        const hasAIError = post.metadata?.aiProcessingStatus === 'error';
        const aiErrorMessage = post.metadata?.aiProcessingError;
        const isProcessing = post.content.nsHtml.includes("Processing your request");
        const hasSavedContent = post.content?.nsHtml && post.content.nsHtml.length > 0 && !isProcessing;

        // Use the streaming state hook - uses SSE streamingComplete flag directly
        const { isStreaming, isStreamingPost, hasStreamingEnded, streamingText } = usePostStreamingState({
            postId: post._id,
        });

        // Auto-play triggers when:
        // 1. Auto-play is enabled
        // 2. This is a new AI message
        // 3. Streaming has ended (SSE onComplete fired for this post)
        const shouldAutoPlayAudio = autoPlayAudio && isNewMessage && hasStreamingEnded;

        // Determine the content to display and for TTS
        const messageContent = hasSavedContent 
            ? post.content.nsHtml 
            : (streamingText || '');
        
        const hasContent = messageContent.trim().length > 0;
        
        // Show TTS player:
        // - For streaming posts: only after streaming completes
        // - For old posts (not streaming): show immediately if content exists
        const showTTSPlayer = hasContent && !hasAIError && !!post._id && 
            (isStreamingPost ? hasStreamingEnded : true);

        // User message (right side)
        if (boxOrientation === "right") {
            return (
                <VStack align={isRTL ? "start" : "end"} width="100%" maxWidth="100%" boxSizing="border-box" gap={1}>
                    <HStack justify={isRTL ? "start" : "end"} padding={{ base: "12px 16px", md: 4 }} width="100%" maxWidth="100%" boxSizing="border-box">
                        <Card.Root maxWidth={{ base: "100%", md: "90%" }} background="var(--cair-premium-subtle)" borderRadius="var(--cair-radius-md)" border="0" boxSizing="border-box">
                            <Card.Body gap="1" paddingY="2">
                                <HStack>
                                    <Text paddingLeft="2" fontSize="xs" marginLeft="auto" textAlign="right" color="gray.500">
                                        {formatDate(post.created)}
                                    </Text>
                                </HStack>
                                <Box className="chakra-card__description" css={{ color: "fg.muted" }} paddingY="0">
                                    <SmartProse htmlContent={post.content.nsHtml} dir="auto" maxWidth="unset">
                                        <div dangerouslySetInnerHTML={{ __html: sanitizeMalformedLinks(post.content.nsHtml) }} />
                                    </SmartProse>
                                </Box>
                            </Card.Body>
                        </Card.Root>
                    </HStack>

                    {post?.filterMetadata?.searchFilters && (
                        <HStack justify={isRTL ? "start" : "end"} width="100%" maxWidth="100%" paddingX={{ base: "16px", md: 4 }} paddingBottom={1}>
                            <Box maxWidth={{ base: "100%", md: "90%" }}>
                                <FilterChips
                                    filters={post?.filterMetadata?.searchFilters}
                                    suggestions={filterSuggestions}
                                    messageContent={post.content.nsHtml}
                                    onEditClick={() => {
                                        const event = new CustomEvent('editFilters', {
                                            detail: { message: post.content.nsHtml, filters: post?.filterMetadata?.searchFilters }
                                        });
                                        window.dispatchEvent(event);
                                    }}
                                />
                            </Box>
                        </HStack>
                    )}
                </VStack>
            );
        }

        // AI message (left side)
        return (
            <HStack padding={{ base: "12px 16px", md: 4 }} width="100%" maxWidth="100%" boxSizing="border-box">
                <Card.Root width="100%" maxWidth={{ base: "100%", md: "90%" }} variant="outline" background="transparent" border="0" boxSizing="border-box">
                    <Card.Body gap="2">
                        {hasAIError && <ErrorMessage message={aiErrorMessage} isRTL={isRTL} />}
                        
                        {!hasAIError && post.vector_search_results && post.vector_search_results.length > 0 && (
                            <SourcesAccordion sources={post.vector_search_results as VectorSearchChunk[]} postId={post._id} isRTL={isRTL} />
                        )}
                        
                        {/* Render content: streaming text while streaming, saved content when done */}
                        {!hasAIError && (
                            isStreaming 
                                ? <AIMessageContent content={streamingText} isStreaming={true} />
                                : hasSavedContent 
                                    ? <AIMessageContent content={post.content.nsHtml} isStreaming={false} />
                                    : streamingText && <AIMessageContent content={streamingText} isStreaming={false} />
                        )}

                        {/* TTS Audio Player */}
                        {showTTSPlayer && (
                            <Box width="100%" paddingTop="2" borderTop="1px solid" borderColor="gray.100">
                                <TTSAudioPlayer
                                    contentId={post._id}
                                    contentType="post"
                                    textContent={messageContent}
                                    shouldAutoPlay={shouldAutoPlayAudio}
                                    isNewMessage={isNewMessage}
                                />
                            </Box>
                        )}
                    </Card.Body>
                </Card.Root>
            </HStack>
        );
    }
);
